import { useEffect, useRef, useState } from 'react';
import { loadNote, saveNote } from '@/lib/zcall';
import { createDocumentWithContent, updateDocument } from '@/lib/documents';

interface Props {
  roomId: string;
  sessionKey: string;
  userId: number | null;
  userName: string;
  meetingTitle: string;
  /** Ouvre le formulaire de création de compte sans quitter la réunion. */
  onRequireAccount: () => void;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function textToHtml(text: string): string {
  return `<p>${escapeHtml(text).replace(/\n/g, '<br>')}</p>`;
}

/**
 * Bloc-notes de la réunion.
 *
 * Lorsque l'utilisateur est connecté, le bloc-notes est un VRAI FICHIER WORD
 * créé dans « /dashboard/documents » (au premier clic) et mis à jour
 * automatiquement — il reste donc disponible après la visioconférence.
 * S'il n'est pas connecté, une notice lui propose de créer un compte sans
 * quitter la salle.
 */
export default function NotepadPanel({ roomId, sessionKey, userId, userName, meetingTitle, onRequireAccount }: Props) {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [docId, setDocId] = useState<number | null>(null);
  const timer = useRef<number | null>(null);
  const docIdRef = useRef<number | null>(null);
  docIdRef.current = docId;

  // Chargement + création du fichier Word si nécessaire.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const note = await loadNote(roomId, sessionKey);
      if (cancelled) return;
      const initial = note?.content || '';
      setContent(initial);
      setDocId(note?.document_id ?? null);

      // Utilisateur connecté sans document Word associé → on le crée.
      if (userId && !note?.document_id) {
        try {
          const title = `Bloc-notes — ${meetingTitle || 'Réunion ZCall'}`.slice(0, 120);
          const doc = await createDocumentWithContent(userId, title, 'word', { html: textToHtml(initial) });
          if (!cancelled) {
            setDocId(doc.id);
            await saveNote(roomId, sessionKey, { userId, userName, content: initial, documentId: doc.id });
          }
        } catch {
          /* best-effort : le bloc-notes reste utilisable en local */
        }
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [roomId, sessionKey, userId, userName, meetingTitle]);

  const persist = async (value: string) => {
    setSaving(true);
    await saveNote(roomId, sessionKey, {
      userId,
      userName,
      content: value,
      documentId: docIdRef.current,
    });
    const id = docIdRef.current;
    if (id) {
      try {
        await updateDocument(id, { html: textToHtml(value) }, undefined, userId ? { id: userId, name: userName } : undefined);
      } catch {
        /* best-effort */
      }
    }
    setSaving(false);
    setSavedAt(new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }));
  };

  const schedule = (value: string) => {
    setContent(value);
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => { void persist(value); }, 800);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <div className="flex items-center gap-2">
          <i className="ri-sticky-note-line text-accent-400"></i>
          <span className="text-sm font-semibold text-white">Bloc-notes</span>
          {userId && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-accent-500/25 text-accent-100 font-medium">Word</span>}
        </div>
        <span className="text-[10px] text-white/50">
          {loading ? 'Chargement…' : saving ? 'Enregistrement…' : savedAt ? `Enregistré ${savedAt}` : 'Auto-sauvegarde'}
        </span>
      </div>

      {!userId && (
        <div className="mx-3 mt-3 px-3 py-2.5 rounded-lg bg-white/5 border border-white/10">
          <p className="text-[11px] text-white/70 leading-snug">
            Créez un compte pour enregistrer ce bloc-notes dans vos documents et le retrouver après la réunion.
          </p>
          <button
            onClick={onRequireAccount}
            className="mt-2 w-full py-2 rounded-full bg-accent-500 text-white text-xs font-semibold cursor-pointer hover:bg-accent-600 transition-colors whitespace-nowrap"
          >
            Créer un compte
          </button>
        </div>
      )}

      <textarea
        value={content}
        onChange={(e) => schedule(e.target.value)}
        placeholder="Notes de la réunion — ordre du jour, décisions, tâches à suivre…"
        className="flex-1 w-full p-4 bg-transparent text-sm text-white/90 placeholder:text-white/30 resize-none focus:outline-none leading-relaxed"
      />

      <div className="px-4 py-2 border-t border-white/10 text-[10px] text-white/40 flex items-center gap-1.5">
        <i className={userId ? 'ri-file-word-2-line' : 'ri-lock-line'}></i>
        {userId
          ? 'Fichier Word enregistré dans Documents — disponible après la réunion.'
          : 'Rattaché à cet appareil — créez un compte pour le conserver.'}
      </div>
    </div>
  );
}